/**
 * System lifecycle lines of the live-chat thread: which prefix gets which label, and the "vendor agreed to RFI's
 * counter" rules (VE-AGREE). Pure helpers, no Angular, so they can be unit-tested.
 */

export interface SystemLifecycleRule {
  kind: string;
  prefix: string;
  label: string;
}

/** First line of the system line posted when the vendor agrees to RFI's counter (server contract, VE-AGREE). */
export const VENDOR_AGREED_PREFIX = "Vendor agreed to RFI's counter";

export const VENDOR_AGREED_KIND = 'vendor-agreed';

/** First sentence of the line posted when the vendor sends changes back to RFI's counter (server: VendorSentBackChatLine). */
export const VENDOR_SENT_BACK_PREFIX = "The vendor changed RFI's counter and sent it back";

export const VENDOR_SENT_BACK_KIND = 'vendor-sent-back';

/** Banner shown on the estimate while the vendor's agreement waits for RFI. */
export const VENDOR_AGREED_BANNER_TEXT =
  'The vendor agreed to your counter. Nothing was changed. Approve it to finish, or send it to the customer or keep negotiating.';

/**
 * Notes on the Negotiate tab (26 Oct): after the vendor agreed, the agent's suggestions start from the vendor's own
 * numbers again, so they can match the counter the vendor already accepted.
 */
export const NEGOTIATE_VENDOR_AGREED_NOTE =
  'The vendor already agreed to your last counter. The suggestions below may be the same as that counter. To go lower, press Edit and type your own price. To finish, approve.';

/** Shown when the agent has stopped countering (the vendor refused its final offer, or the safety limit was reached). */
export const NEGOTIATE_NO_MORE_COUNTERS_NOTE =
  'The agent has no more counters. Edit the prices yourself or message the vendor.';

/** Line flag codes the agent sends when it will not counter again (negotiator release 26 Oct). */
const NO_MORE_COUNTER_FLAG_PARTS = ['final_offer_refused', 'flow_safety_limit'];

/** True when any line carries a flag saying the agent has no more counters. Flag codes are open text: match by part. */
export function agentHasNoMoreCounters(
  lines: ReadonlyArray<{ flags?: ReadonlyArray<{ code?: string | null } | null> | null }> | null | undefined,
): boolean {
  if (!lines) return false;
  return lines.some((line) =>
    (line.flags ?? []).some((flag) => {
      const code = (flag?.code ?? '').toLowerCase();
      return NO_MORE_COUNTER_FLAG_PARTS.some((part) => code.includes(part));
    }),
  );
}

/**
 * Lifecycle lines that get their own label. "RFI is going ahead with <option>. Your other options are now
 * closed: ..." (multi-option on-site approvals, 8 Oct) is deliberately not listed: it is a plain "Update" line
 * and doesn't change the phase; the action's own line follows it.
 *
 * The vendor-agreed rule comes first on purpose: it must never fall through to a generic "Update" label.
 */
export const SYSTEM_LIFECYCLE_RULES: SystemLifecycleRule[] = [
  { kind: VENDOR_AGREED_KIND, prefix: VENDOR_AGREED_PREFIX, label: 'Vendor agreed' },
  { kind: VENDOR_SENT_BACK_KIND, prefix: VENDOR_SENT_BACK_PREFIX, label: 'Vendor sent back' },
  {
    kind: 'submitted',
    prefix: 'Your RFI Contact is reviewing your on-site approval request',
    label: 'On-site request',
  },
  { kind: 'submitted', prefix: 'Your on-site approval request was submitted', label: 'On-site request' },
  {
    kind: 'reviewing',
    prefix: 'Your on-site approval request is being reviewed',
    label: 'Opened by RFI',
  },
  { kind: 'reviewing', prefix: 'Your estimate is being reviewed', label: 'Opened by RFI' },
  { kind: 'action-required', prefix: 'RFI has updated your estimate', label: 'Action required' },
  {
    kind: 'vendor-accepted',
    prefix: "You've received your approval to proceed",
    label: 'Approved by vendor',
  },
  {
    kind: 'vendor-accepted',
    prefix: 'Vendor accepted the updated estimate',
    label: 'Approved by vendor',
  },
  {
    kind: 'vendor-accepted',
    prefix: 'You accepted the updated estimate',
    label: 'Approved by vendor',
  },
  {
    kind: 'approved',
    prefix: 'Your on-site approval estimate has been approved',
    label: 'Approved',
  },
  { kind: 'changes-requested', prefix: 'Changes were requested', label: 'Changes requested' },
  {
    kind: 'declined',
    prefix: 'Your on-site approval estimate has been declined',
    label: 'Declined',
  },
  {
    kind: 'site-update',
    prefix: 'Your on-site approval will not be completed today',
    label: 'Site update',
  },
];

function firstLine(text: string | null | undefined): string {
  return (text || '').split('\n')[0].trim();
}

function startsWithIgnoreCase(line: string, prefix: string): boolean {
  return line.toLowerCase().startsWith(prefix.toLowerCase());
}

/** Rule for a system line, or the generic "Update" fallback. The vendor-agreed prefix is matched case-insensitively. */
export function resolveSystemLifecycleRule(text: string | null | undefined): SystemLifecycleRule {
  const first = firstLine(text);
  for (const rule of SYSTEM_LIFECYCLE_RULES) {
    if (rule.kind === VENDOR_AGREED_KIND ? startsWithIgnoreCase(first, rule.prefix) : first.startsWith(rule.prefix)) {
      return rule;
    }
  }
  return { kind: 'update', prefix: '', label: 'Update' };
}

/** True for any system line that has its own lifecycle label (i.e. is not a plain "Update"). */
export function isLifecycleLine(text: string | null | undefined): boolean {
  return resolveSystemLifecycleRule(text).kind !== 'update';
}

export function isVendorAgreedLine(text: string | null | undefined): boolean {
  return resolveSystemLifecycleRule(text).kind === VENDOR_AGREED_KIND;
}

/**
 * Nahid (4 Nov): these lines are written to the vendor ("Your RFI Contact is reviewing your request now"), so an account
 * manager read the request as already being handled before anyone had opened it. The admin sees what actually happened.
 * Matched on the first line; anything after it (an admin's note) is kept.
 */
const ADMIN_VIEW_FIRST_LINES: ReadonlyArray<{ prefix: string; text: string }> = [
  {
    prefix: 'Your RFI Contact is reviewing your on-site approval request',
    text: 'The vendor sent an on-site approval request and is waiting on site for your decision.',
  },
  {
    prefix: 'Your on-site approval request was submitted',
    text: 'The vendor sent an on-site approval request and is waiting on site for your decision.',
  },
  {
    prefix: 'Your on-site approval request is being reviewed',
    text: 'RFI opened the request. The vendor was told it is being reviewed. It still needs your decision.',
  },
  {
    prefix: 'Your estimate is being reviewed',
    text: 'RFI opened the estimate. The vendor was told it is being reviewed. It still needs your decision.',
  },
  {
    prefix: 'The customer approval was not sent. Your RFI contact is still reviewing',
    text: 'The customer approval was not sent. The on-site request is back with RFI for a decision.',
  },
];

/**
 * Admin-facing text of a system line. The old vendor-accepted lines are rewritten for the admin IM
 * ("Vendor approved the negotiated on-site approval"), and the vendor-worded "reviewing" lines say what happened
 * (ADMIN_VIEW_FIRST_LINES); every other line, including the vendor-agreed line, is shown as stored.
 */
export function systemLifecycleDisplayText(text: string, vendorAcceptedAdminText: string): string {
  if (resolveSystemLifecycleRule(text).kind === 'vendor-accepted') return vendorAcceptedAdminText;
  const [first, ...rest] = (text || '').split('\n');
  const rewrite = ADMIN_VIEW_FIRST_LINES.find((r) => first.trim().startsWith(r.prefix));
  return rewrite ? [rewrite.text, ...rest].join('\n') : text;
}

/**
 * Informational lifecycle kinds: they say RFI is looking at it ("... is being reviewed now", the automatic line the
 * server posts right after the vendor agrees) and decide nothing, so they never end the vendor-agreed state.
 */
const INFORMATIONAL_LIFECYCLE_KINDS: ReadonlySet<string> = new Set(['reviewing']);

/**
 * True when the newest DECISION lifecycle line of the thread (system lines only; plain "Update" lines and the
 * informational "being reviewed" lines are skipped) is the vendor-agreed line. A later RFI counter, vendor
 * submit/send-back, approval, rejection, decline or customer-approval line makes it false again.
 */
export function newestLifecycleLineIsVendorAgreed(
  messages: ReadonlyArray<{ senderType: number; text: string }>,
): boolean {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m.senderType !== 3) continue;
    const rule = resolveSystemLifecycleRule(m.text);
    if (rule.kind === 'update' || INFORMATIONAL_LIFECYCLE_KINDS.has(rule.kind)) continue;
    return rule.kind === VENDOR_AGREED_KIND;
  }
  return false;
}

/**
 * Status 0 is pending: the estimate still awaits RFI. The server keeps it at 0 after the vendor agrees; approving
 * (1) or sending it to the customer (on-site becomes 2) moves it on, and the banner must go.
 */
const ESTIMATE_STATUS_PENDING = 0;

/**
 * The vendor-agreed banner shows only while the vendor's agreement is the newest lifecycle line AND the loaded
 * thread estimate still awaits RFI (status 0, edited by the vendor, not cancelled). It is decided by the thread's
 * estimate, not by the line alone: the shared thread may also carry an agreed line for another estimate.
 */
export function shouldShowVendorAgreedBanner(
  newestLineIsVendorAgreed: boolean,
  estimate: { editedByVendor?: boolean; status?: number; isCancelled?: boolean } | null | undefined,
): boolean {
  if (!newestLineIsVendorAgreed || !estimate) return false;
  if (!estimate.editedByVendor) return false;
  if (estimate.isCancelled) return false;
  return estimate.status === ESTIMATE_STATUS_PENDING;
}

/** Round label in the negotiation history list. */
export function negotiationRoundActionLabel(action: string | null | undefined, party?: string | null): string {
  switch (action) {
    case 'submitted':
      return 'Vendor submitted';
    case 'counter':
      return 'RFI counter';
    case 'send_back':
      return 'Vendor sent back changes';
    case 'vendor_accepted':
    case 'vendor_agreed':
      return "Vendor agreed to RFI's counter";
    case 'approved':
      return party === 'vendor' ? "Vendor approved RFI's estimate" : 'Approved by RFI';
    case 'declined':
      return 'Declined';
    case 'rejected':
      return 'Rejected for resubmission';
    default: {
      const text = (action || 'Update').replace(/_/g, ' ');
      return text.charAt(0).toUpperCase() + text.slice(1);
    }
  }
}
